import { IdentityStoreType } from '@appState';
import { NavExpandNode } from 'cui-components';

export function setupAuthorizationMenu(identityStore: IdentityStoreType): NavExpandNode[] {
  const navigationItems: NavExpandNode[] = [];

  const hasAuthorizationMenuPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Authorization.MenuAccess']);
  const hasPermissionsMenuPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Authorization.Permissions.MenuAccess']);
  const hasRolesMenuPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Authorization.Roles.MenuAccess']);
  const hasRoleGroupsMenuPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Authorization.RoleGroups.MenuAccess']);
  const hasImportChangesMenuPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Authorization.ImportChanges.MenuAccess']);

  if (hasAuthorizationMenuPermission) {
    navigationItems.push({
      id: '1',
      parentId: null,
      name: 'Authorization',
      icon: 'lock.svg',
      children: [],
    });

    if (hasRoleGroupsMenuPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        id: '1-1',
        parentId: '1',
        name: 'Role groups',
        icon: 'groups_3',
        url: '/role-groups',
      });
    }

    if (hasRolesMenuPermission && navigationItems[0].children) {
      navigationItems[0].children.push({ id: '1-2', parentId: '1', name: 'Roles', icon: 'workspaces', url: '/roles' });
    }

    if (hasPermissionsMenuPermission && navigationItems[0].children) {
      navigationItems[0].children.push({ id: '1-3', parentId: '1', name: 'Permissions', url: '/permissions', icon: 'key' });
    }

    if (hasImportChangesMenuPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        id: '1-4',
        parentId: '1',
        name: 'Import changes',
        url: '/import-changes',
        icon: 'upload_file',
      });
    }
  }

  return navigationItems;
}

export function setupActionsMenu(identityStore: IdentityStoreType): NavExpandNode[] {
  const navigationItems: NavExpandNode[] = [];
  const hasActionsMenuPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Actions.MenuAccess']);

  if (hasActionsMenuPermission) {
    navigationItems.push({
      id: '2',
      parentId: null,
      name: 'Actions',
      icon: 'touch.svg',
      children: [],
    });

    const hasActionLogsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Actions.Logs.MenuAccess']);
    const hasActionTypesPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Actions.Types.MenuAccess']);

    if (hasActionLogsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({ id: '2-1', parentId: '2', name: 'Logs', icon: 'troubleshoot', url: '/actions/logs' });
    }

    if (hasActionTypesPermission && navigationItems[0].children) {
      navigationItems[0].children.push({ id: '2-2', parentId: '2', name: 'Types', icon: 'checklist_rtl', url: '/actions/types' });
    }
  }

  return navigationItems;
}

export function setupCommonDataMenu(identityStore: IdentityStoreType): NavExpandNode[] {
  const navigationItems: NavExpandNode[] = [];

  const hasCommonDataPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.CommonData.MenuAccess']);

  if (hasCommonDataPermission) {
    navigationItems.push({
      id: '3',
      parentId: null,
      name: 'Common data',
      icon: 'data.svg',
      children: [],
    });

    const hasRegionsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.CommonData.Regions.MenuAccess']);
    const hasMembershipLevelsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.CommonData.MembershipLevels.MenuAccess']);
    const hasLanguagesPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.CommonData.Languages.MenuAccess']);
    const hasCountriesPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.CommonData.Countries.MenuAccess']);

    if (hasRegionsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Regions',
        parentId: '3',
        id: '3-1',
        url: '/common-data/regions',
        icon: 'location_on',
      });
    }

    if (hasMembershipLevelsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Membership levels',
        parentId: '3',
        id: '3-2',
        url: '/common-data/membership-levels',
        icon: 'stars',
      });
    }

    if (hasLanguagesPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Languages',
        parentId: '3',
        id: '3-3',
        url: '/common-data/languages',
        icon: 'translate',
      });
    }

    if (hasCountriesPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Countries',
        parentId: '3',
        id: '3-4',
        url: '/common-data/countries',
        icon: 'public',
      });
    }

    if (navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Common data',
        parentId: '3',
        id: '3-5',
        url: '/common-data/common-data',
        icon: 'dataset',
      });
    }
  }

  return navigationItems;
}

export function setupMarketingMenu(identityStore: IdentityStoreType): NavExpandNode[] {
  const navigationItems: NavExpandNode[] = [];

  const hasMarketingPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Marketing.MenuAccess']);

  if (hasMarketingPermission) {
    navigationItems.push({
      id: '4',
      parentId: null,
      name: 'Marketing',
      icon: 'positive_dynamic.svg',
      children: [],
    });

    const hasAnnouncementsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Marketing.Announcements.MenuAccess']);
    const hasNewsArticlesPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Marketing.NewsArticles.MenuAccess']);
    const hasEventsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Marketing.Events.MenuAccess']);
    const hasRecordingsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Marketing.Recordings.MenuAccess']);
    const hasDocumentsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Marketing.Documents.MenuAccess']);
    const hasTopicsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Marketing.Topics.MenuAccess']);
    const hasCommentsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Marketing.Comments.MenuAccess']);
    const hasCommunitiesPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Marketing.Communities.MenuAccess']);
    const hasCategoriesPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Marketing.Categories.MenuAccess']);
    const hasFieldExpertsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Marketing.FieldExperts.MenuAccess']);

    if (hasCommunitiesPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Communities',
        parentId: '4',
        id: '4-1',
        isFirstWithGroupName: true,
        groupName: 'Information Hub',
        url: '/marketing/communities',
        icon: 'groups',
      });
    }

    if (hasAnnouncementsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Announcements',
        parentId: '4',
        groupName: 'Information Hub',
        id: '4-2',
        url: '/marketing/announcements',
        icon: 'announcement',
      });
    }

    if (hasNewsArticlesPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'News',
        parentId: '4',
        id: '4-3',
        groupName: 'Information Hub',
        url: '/marketing/news-articles',
        icon: 'feed',
      });
    }

    if (hasEventsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Events',
        parentId: '4',
        id: '4-4',
        groupName: 'Information Hub',
        url: '/marketing/events',
        icon: 'event',
      });
    }

    if (hasRecordingsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Recordings',
        parentId: '4',
        id: '4-5',
        groupName: 'Information Hub',
        url: '/marketing/recordings',
        icon: 'movie',
      });
    }

    if (hasCommentsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Comments',
        parentId: '4',
        id: '4-6',
        groupName: 'Information Hub',
        url: '/marketing/news-articles-comments',
        icon: 'forum',
      });
    }

    if (hasDocumentsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Documents',
        parentId: '4',
        id: '4-7',
        isFirstWithGroupName: !hasCommunitiesPermission,
        groupName: 'Information Hub',
        url: '/marketing/documents',
        icon: 'description',
      });
    }

    if (hasTopicsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Topics',
        parentId: '4',
        id: '4-8',
        isFirstWithGroupName: true,
        groupName: 'Configuration',
        url: '/marketing/topics',
        icon: 'format_list_bulleted',
      });
    }

    if (hasFieldExpertsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Field experts',
        parentId: '4',
        id: '4-9',
        isFirstWithGroupName: !hasTopicsPermission,
        groupName: 'Configuration',
        url: '/marketing/field-experts',
        icon: 'people',
      });
    }

    if (hasCategoriesPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Categories',
        parentId: '4',
        id: '4-10',
        isFirstWithGroupName: !hasTopicsPermission,
        groupName: 'Configuration',
        url: '/marketing/categories',
        icon: 'category',
      });
    }
  }

  return navigationItems;
}

export function setupFileManagementMenu(identityStore: IdentityStoreType): NavExpandNode[] {
  const navigationItems: NavExpandNode[] = [];

  const hasFileManagementPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.FileManagement.MenuAccess']);

  if (hasFileManagementPermission) {
    navigationItems.push({
      id: '5',
      parentId: null,
      name: 'File management',
      icon: 'files.svg',
      children: [],
    });

    const hasBranchesPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.FileManagement.Branches.MenuAccess']);
    const hasFilesPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.FileManagement.Files.MenuAccess']);

    if (hasBranchesPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Branches',
        parentId: '5',
        id: '5-1',
        url: '/file-management/branches',
        icon: 'settings',
      });
    }

    if (hasFilesPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Files',
        parentId: '5',
        id: '5-2',
        url: '/file-management/files',
        icon: 'description',
      });
    }
  }

  return navigationItems;
}

export function setupProductsAndOrdersMenu(identityStore: IdentityStoreType): NavExpandNode[] {
  const navigationItems: NavExpandNode[] = [];

  const hasProductsAndOrdersPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.ProductsAndOrders.MenuAccess']);

  if (hasProductsAndOrdersPermission) {
    navigationItems.push({
      id: '6',
      parentId: null,
      name: 'Products & Orders',
      icon: 'tasks.svg',
      children: [],
    });

    const hasProductsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.ProductsAndOrders.Products.MenuAccess']);
    const hasCustomerReportsPermission = identityStore.hasPermissionForMenuAccess([
      'Portal.Admin.ProductsAndOrders.CustomerReports.MenuAccess',
    ]);
    const hasReportedCustomersPermission = identityStore.hasPermissionForMenuAccess([
      'Portal.Admin.ProductsAndOrders.ReportedCustomers.MenuAccess',
    ]);
    const hasRequestsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.ProductsAndOrders.Requests.MenuAccess']);
    const hasLinesPreFillPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.ProductsAndOrders.LinesPreFill.MenuAccess']);
    const hasOrdersPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.ProductsAndOrders.Orders.MenuAccess']);
    const hasEmpoweredLicensesPermission = identityStore.hasPermissionForMenuAccess([
      'Portal.Admin.ProductsAndOrders.EmpoweredLicenses.MenuAccess',
    ]);
    const hasLicenseKeyGenerationPermission = identityStore.hasPermissionForMenuAccess([
      'Portal.Admin.ProductsAndOrders.LicenseKeyGeneration.MenuAccess',
    ]);

    if (hasProductsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Products',
        parentId: '6',
        id: '6-1',
        url: '/products',
        icon: 'shopping_bag',
      });
    }

    if (hasOrdersPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Orders',
        parentId: '6',
        id: '6-2',
        url: '/orders',
        icon: 'shopping_cart',
      });
    }

    if (hasCustomerReportsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Customer reports',
        parentId: '6',
        id: '6-4',
        url: '/customer-reports',
        icon: 'description',
      });
    }

    if (hasReportedCustomersPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Reported customers',
        parentId: '6',
        id: '6-5',
        url: '/reported-customers',
        icon: 'groups',
      });
    }

    if (hasLinesPreFillPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Lines pre-fill',
        parentId: '6',
        id: '6-6',
        url: '/lines-prefill',
        icon: 'document_scanner',
      });
    }

    if (hasRequestsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Requests',
        parentId: '6',
        id: '6-7',
        url: '/requests',
        icon: 'request_quote',
        groupName: 'Product requests',
        isFirstWithGroupName: true,
      });
    }
    if (hasEmpoweredLicensesPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Empowered licenses',
        parentId: '6',
        id: '6-8',
        url: '/empowered-licenses',
        icon: 'paid',
        groupName: 'Product requests',
      });
    }

    if (hasLicenseKeyGenerationPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Key generation',
        parentId: '6',
        id: '6-9',
        icon: 'vpn_key',
        groupName: 'Product requests',
        customAction: 'LicenseKeyGeneration',
      });
    }
  }

  return navigationItems;
}

export function setupNotificationsMenu(identityStore: IdentityStoreType): NavExpandNode[] {
  const navigationItems: NavExpandNode[] = [];

  const hasNotificationsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Notifications.MenuAccess']);

  if (hasNotificationsPermission) {
    navigationItems.push({
      id: '7',
      parentId: null,
      name: 'Notifications',
      icon: 'bell.svg',
      children: [],
    });

    const hasReceiverGroupsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Notifications.ReceiverGroups.MenuAccess']);
    const hasExternalTemplatePermission = identityStore.hasPermissionForMenuAccess([
      'Portal.Admin.Notifications.ExternalTemplates.MenuAccess',
    ]);
    const hasTemplateGroupsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Notifications.TemplateGroups.MenuAccess']);
    const hasTemplatePermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Notifications.Templates.MenuAccess']);
    const hasNotificationLogsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Notifications.Logs.MenuAccess']);

    if (hasTemplateGroupsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Template groups',
        parentId: '7',
        id: '7-1',
        url: '/notifications/template-groups',
        icon: 'mark_as_unread',
      });
    }

    if (hasTemplatePermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Templates',
        parentId: '7',
        id: '7-2',
        url: '/notifications/templates',
        icon: 'drafts',
      });
    }

    if (hasExternalTemplatePermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'External templates',
        parentId: '7',
        id: '7-3',
        url: '/notifications/external-templates',
        icon: 'attach_email',
      });
    }

    if (hasReceiverGroupsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Receiver groups',
        parentId: '7',
        id: '7-4',
        url: '/notifications/groups',
        icon: 'menu',
      });
    }

    if (hasNotificationLogsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Logs',
        parentId: '7',
        id: '7-5',
        url: '/notifications/logs',
        icon: 'troubleshoot',
      });
    }
  }

  return navigationItems;
}

export function setupCspMenu(identityStore: IdentityStoreType): NavExpandNode[] {
  const navigationItems: NavExpandNode[] = [];

  const hasCspPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Csp.MenuAccess']);

  if (hasCspPermission) {
    navigationItems.push({
      id: '8',
      parentId: null,
      name: 'CSP',
      icon: 'cloud_management.svg',
      children: [],
    });

    const hasEntraAppsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Csp.EntraApps.MenuAccess']);
    const hasTenantsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Csp.Tenants.MenuAccess']);
    const hasMpnIdCheckPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Csp.MpnIdCheck.MenuAccess']);
    const hasLicenseTypesPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Csp.LicenseTypes.MenuAccess']);
    const hasLicenseOffersPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Csp.LicenseOffers.MenuAccess']);

    if (hasTenantsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Tenants',
        parentId: '8',
        id: '8-1',
        url: '/csp/tenants',
        icon: 'assignment_ind',
      });
    }

    if (hasMpnIdCheckPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'MPN ID check',
        parentId: '8',
        id: '8-2',
        icon: 'person_search',
        customAction: 'MpnIdCheck',
      });
    }

    if (hasLicenseTypesPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'License types',
        parentId: '8',
        id: '8-3',
        url: '/csp/license-types',
        icon: 'format_list_bulleted',
      });
    }

    if (hasLicenseOffersPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Offers',
        parentId: '8',
        id: '8-4',
        url: '/csp/offers',
        icon: 'paid',
      });
    }

    if (hasEntraAppsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Entra apps',
        parentId: '8',
        id: '8-5',
        url: '/csp/entra-apps',
        icon: 'apps',
      });
    }

    const hasMpnMappingsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Csp.MpnMappings.MenuAccess']);

    if (hasMpnMappingsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'MPN mappings',
        parentId: '8',
        id: '8-6',
        url: '/csp/mpn-mappings',
        icon: 'compare_arrows',
      });
    }
  }

  return navigationItems;
}

export function setupMonitoringMenu(identityStore: IdentityStoreType): NavExpandNode[] {
  const navigationItems: NavExpandNode[] = [];

  const hasMonitoringPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Monitoring.MenuAccess']);

  if (hasMonitoringPermission) {
    navigationItems.push({
      id: '9',
      parentId: null,
      name: 'Monitoring',
      icon: 'monitoring.svg',
      children: [],
    });

    const hasAlertTypesPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Monitoring.AlertTypes.MenuAccess']);

    if (hasAlertTypesPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Alert types',
        parentId: '9',
        id: '9-1',
        url: '/monitoring/alert-types',
        icon: 'notification_important',
      });
    }

    const hasConfiguredAlertsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Monitoring.ConfiguredAlerts.MenuAccess']);

    if (hasConfiguredAlertsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Configured alerts',
        parentId: '9',
        id: '9-2',
        url: '/monitoring/configured-alerts',
        icon: 'notifications_active',
      });
    }

    const hasAlertLogsPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.Monitoring.Logs.MenuAccess']);

    if (hasAlertLogsPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        name: 'Logs',
        parentId: '9',
        id: '9-3',
        url: '/monitoring/logs',
        icon: 'troubleshoot',
      });
    }
  }

  return navigationItems;
}

export function setupJobOrchestratorMenu(identityStore: IdentityStoreType): NavExpandNode[] {
  const navigationItems: NavExpandNode[] = [];

  const hasMenuPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.JobOrchestrator.MenuAccess']);

  if (hasMenuPermission) {
    navigationItems.push({
      id: '10',
      parentId: null,
      name: 'Job Orchestrator',
      icon: 'job-orchestrator.svg',
      children: [],
    });

    const hasWorkflowsMenuPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.JobOrchestrator.Workflows.MenuAccess']);

    if (hasWorkflowsMenuPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        id: '10-1',
        parentId: '10',
        name: 'Workflows',
        icon: 'schema',
        url: '/job-orchestrator/workflows',
      });
    }

    const hasOperationsMenuPermission = identityStore.hasPermissionForMenuAccess(['Portal.Admin.JobOrchestrator.Operations.MenuAccess']);

    if (hasOperationsMenuPermission && navigationItems[0].children) {
      navigationItems[0].children.push({
        id: '10-2',
        parentId: '10',
        name: 'Operations',
        icon: 'checklist',
        url: '/job-orchestrator/operations',
      });
    }
  }

  return navigationItems;
}
